import AlphaFilters from './alpha-filters'
import DOMPurify from 'dompurify'

export default class AlphaHelpers {
  static svgUniqIdTimeoutId = null
  static svgUniqIdExists = []
  static fetchResponse = new Map()
  static fetchPending = new Map()

  static objectHasAnyValues(obj, strict = true) {
    // also check if object isn't an array in strict mode (because typeof array returns 'object' in JS)
    if (typeof obj === 'object' && (!strict || !Array.isArray(obj))) {
      return Object.keys(obj).length > 0
    }

    return false
  }

  static objectHasOwnProperty(obj, prop) {
    if (typeof obj !== 'object' || Array.isArray(obj)) {
      return false
    }

    return Object.prototype.hasOwnProperty.call(obj, prop)
  }

  static noEmptyArrayValues(parts) {
    return parts.filter((v) => v)
  }

  static getAlphaAttributePrefix() {
    return 'data-alpha'
  }

  static getAlphaAttributeName(attrName) {
    return `${this.getAlphaAttributePrefix()}-${attrName}`
  }

  static getAlphaAttributeSelector(attrName) {
    return `[${this.getAlphaAttributeName(attrName)}]`
  }

  static getGaEventAttributeName(attrName) {
    return `data-ga-event-${attrName}`
  }

  static getBreakpoints() {
    return ['xs', 'sm', 'md', 'lg', 'xl', 'xxl']
  }

  static getBreakpointInfix(bp) {
    switch (bp) {
      case 'xs':
        return ''
      default:
        return `${bp}-`
    }
  }

  static getParsedJsonContent(content) {
    if (typeof content === 'string') {
      try {
        const json = JSON.parse(content)
        if (this.objectHasAnyValues(json, false)) {
          return json
        }
      } catch (e) {
        // eslint-disable-line no-empty
      }
    }

    return null
  }

  static getParsedJsonAttribute(el, attrName) {
    const value = el.getAttribute(attrName)
    if (value) {
      return this.getParsedJsonContent(value)
    }

    return null
  }

  static getModuleStates() {
    return {
      stateNone: 0,
      stateImporting: 1,
      stateLoaded: 2,
      stateLoadingDependencies: 3,
      stateFailedToImport: 4,
      stateFailedMobileOnly: 5,
      stateFailedDesktopOnly: 6
    }
  }

  static getModuleStateString(stateIndex) {
    const moduleStates = this.getModuleStates()
    for (const stateName in moduleStates) {
      if (this.objectHasOwnProperty(moduleStates, stateName)) {
        const value = moduleStates[stateName]
        if (value === stateIndex) {
          return stateName
        }
      }
    }

    return stateIndex
  }

  static getModuleFlags() {
    const flags = {
      none: 0,
      loadMobileOnly: 1,
      loadDesktopOnly: 2
    }

    // eslint-disable-next-line no-bitwise
    flags.loadAnyDevice = flags.loadMobileOnly | flags.loadDesktopOnly

    return flags
  }

  static hasFlag(mask, flags) {
    // eslint-disable-next-line no-bitwise
    return (flags & mask) === mask
  }

  static isAbsoluteUrl(href) {
    return href.match(/^(https?:)?\/\//i)
  }

  static isNumber(value) {
    if (typeof value !== 'undefined') {
      return /^-?[0-9]+(e[0-9]+)?(\.[0-9]+)?$/.test(value)
    }

    return false
  }

  static roundFloat(value, precision = 0) {
    value = Number(value)
    if (isNaN(value)) {
      value = Number(1.0)
    }

    return parseFloat(value.toFixed(precision))
  }

  static getSanitizedHtml(html) {
    if (typeof html === 'string') {
      return DOMPurify.sanitize(html)
    }

    return null
  }

  static getLibStates() {
    return {
      stateNone: 0,
      stateLoading: 1,
      stateLoaded: 2
    }
  }

  static getLibStateString(stateIndex) {
    const aosStates = this.getLibStates()
    for (const stateName in aosStates) {
      if (this.objectHasOwnProperty(aosStates, stateName)) {
        const value = aosStates[stateName]
        if (value === stateIndex) {
          return stateName
        }
      }
    }

    return stateIndex
  }

  static getPropertyValue(property, el = null) {
    if (!el) {
      el = document.documentElement
    }

    return getComputedStyle(el).getPropertyValue(property).trim()
  }

  static getWindowWidth() {
    return window.innerWidth || document.documentElement.offsetWidth
  }

  static hasScrollbar() {
    return document.documentElement.clientHeight < document.documentElement.scrollHeight
  }

  static getScrollbarWidth() {
    return this.getWindowWidth() - document.documentElement.offsetWidth
  }

  static smartQuerySelectorAll(selector, el = null) {
    if (!el) {
      el = document
    }

    const hasAnyElement = el.querySelector(selector) !== null
    if (hasAnyElement) {
      return el.querySelectorAll(selector)
    }

    return []
  }

  static loadScript(src, callback = null) {
    const scriptEl = document.createElement('script')
    if (typeof callback === 'function') {
      scriptEl.addEventListener('load', () => callback())
    }

    scriptEl.src = src
    scriptEl.async = true
    document.getElementsByTagName('head')[0].appendChild(scriptEl)
  }

  static isDesktop() {
    return matchMedia('(hover: hover) and (pointer: fine)').matches
  }

  static isMobile() {
    return !this.isDesktop()
  }

  static iOS() {
    return CSS.supports('-webkit-overflow-scrolling', 'touch')
  }

  static getTimeConstants() {
    // note: exported constants must be in milliseconds
    const second = 1000
    const secondsPerMinute = 60
    const minutesPerHour = 60
    const secondsPerHour = secondsPerMinute * minutesPerHour
    const hoursInADay = 24
    const daysInAWeek = 7
    const daysInAMonth = 31
    const daysInAYear = 365
    const dayInMilliseconds = second * secondsPerHour * hoursInADay

    return {
      dayInMilliseconds,
      weekInMilliseconds: dayInMilliseconds * daysInAWeek,
      monthInMilliseconds: dayInMilliseconds * daysInAMonth,
      yearInMilliseconds: dayInMilliseconds * daysInAYear
    }
  }

  static isPassedTime(time) {
    const now = new Date()

    return now.getTime() > time
  }

  static getLocalStorageKeyPrefix() {
    return 'alpha'
  }

  static getPrefixedLocalStorageKey(key) {
    const prefix = this.getLocalStorageKeyPrefix()
    if (key.indexOf(prefix) === 0) {
      return key
    }

    return `${prefix}-${key}`
  }

  static getLocalStorageItem(key) {
    return localStorage.getItem(this.getPrefixedLocalStorageKey(key))
  }

  static getLocalStorageItemWithExpiry(key) {
    const itemStr = this.getLocalStorageItem(key)
    if (!itemStr) {
      return null
    }

    // check if item has expired and remove it if that's the case
    const item = JSON.parse(itemStr)
    if (this.isPassedTime(item.expiry)) {
      this.removeLocalStorageItem(key)
      return null
    }

    return item.value
  }

  static setLocalStorageItem(key, item) {
    localStorage.setItem(this.getPrefixedLocalStorageKey(key), item)
  }

  static setLocalStorageItemWithExpiry(key, value, ttl = null) {
    if (ttl === null) {
      ttl = this.getTimeConstants().dayInMilliseconds
    }

    ttl = parseInt(ttl, 10)
    if (isNaN(ttl)) {
      throw new Error(`Invalid TTL provided (expected number, got "${ttl}").`)
    }

    const now = new Date()
    const item = {
      value,
      expiry: now.getTime() + ttl
    }

    this.setLocalStorageItem(key, JSON.stringify(item))
  }

  static removeLocalStorageItem(key) {
    localStorage.removeItem(this.getPrefixedLocalStorageKey(key))
  }

  static cleanupExpiredLocalStorageItems() {
    // fast check: skip keys that don't belong to Alpha
    const localStorageKeyPrefix = this.getLocalStorageKeyPrefix()
    const keysToCheck = Object.keys(localStorage).filter((v) => v.indexOf(localStorageKeyPrefix) === 0)
    keysToCheck.forEach((key) => {
      if (this.getLocalStorageItemWithExpiry(key) === null) {
        this.removeLocalStorageItem(key)
      }
    })
  }

  static getQueryStringParams() {
    if ('URLSearchParams' in window) {
      return new URLSearchParams(window.location.search)
    }

    return null
  }

  static queueSvgUniqId() {
    clearTimeout(this.svgUniqIdTimeoutId)
    const uniqIdWait = 50
    this.svgUniqIdTimeoutId = setTimeout(() => this.svgUniqId(), uniqIdWait)
  }

  static svgUniqId() {
    const svgUidParsedClass = 'svg-uid-parsed'
    const svgElements = this.smartQuerySelectorAll(`svg:not(.${svgUidParsedClass})`)
    svgElements.forEach((svgEl) => this.svgElementUniqId(svgEl))
  }

  static svgElementUniqId(svgEl, doAction = false) {
    // automatically replace multiple svg id
    // @see https://github.com/iconfu/svg-inject/blob/master/src/svg-inject.js
    const svgUidParsedClass = 'svg-uid-parsed'
    const svgElementUidParsedClass = 'svg-element-uid-parsed'

    const markSvgAsParsed = () => {
      svgEl.classList.add(svgUidParsedClass)

      if (doAction) {
        AlphaFilters.doAction('svgUniqId.element.parsed', svgEl)
      }
    }

    const markElementAsParsed = (el, allElements) => {
      el.classList.add(svgElementUidParsedClass)

      const parsedElements = [...allElements].filter((v) => v.classList.contains(svgElementUidParsedClass))
      const allParsed = allElements.length === parsedElements.length
      if (allParsed) {
        markSvgAsParsed()
      }
    }

    // search all id in svg
    const uniqueIdLength = 8
    const elementsWithId = this.smartQuerySelectorAll('[id]', svgEl)
    if (elementsWithId.length) {
      // remove parsed class from elements before we effectively parse them (useful in case of a standalone copy)
      elementsWithId.forEach((element) => {
        element.classList.remove(svgElementUidParsedClass)
      })

      // parse all elements
      elementsWithId.forEach((element) => {
        const id = element.id

        if (this.svgUniqIdExists.indexOf(id) === -1) {
          this.svgUniqIdExists.push(id)

          // mark this element as parsed
          markElementAsParsed(element, elementsWithId)
        } else {
          const updateIds = () => {
            // generate new id
            const index = this.getUniqueId(uniqueIdLength)
            const newId = `${id}--${index}`

            // update id
            element.id = newId

            // search and replace used old id on all element attributes
            const hashId = `#${id}`
            const search = `url(${hashId})`
            const replace = `url(#${newId})`
            const urlAttributes = ['clip-path', 'fill', 'filter', 'mask']
            const urlAttributesSelector = urlAttributes.map((attrName) => `[${attrName}="${search}"]`).join(',')
            this.smartQuerySelectorAll(urlAttributesSelector, svgEl).forEach((svgElement) => {
              urlAttributes.forEach((urlAttrName) => {
                if (svgElement.hasAttribute(urlAttrName) && svgElement.getAttribute(urlAttrName).indexOf(search) === 0) {
                  svgElement.setAttribute(urlAttrName, replace)
                }
              })
            })

            // replace all xlink:href & href attributes pointing to this id
            const xlinkHrefAttrName = 'xlink:href'
            this.smartQuerySelectorAll(`[*|href="${hashId}"]`, svgEl).forEach((hrefEl) => {
              const hrefAttrName = hrefEl.hasAttribute(xlinkHrefAttrName) ? xlinkHrefAttrName : 'href'
              hrefEl.setAttribute(hrefAttrName, `#${newId}`)
            })

            // mark this element as parsed
            markElementAsParsed(element, elementsWithId)
          }

          // don't process changes right away, wait for browser to have some idle time
          this.requestIdleCallback(updateIds)
        }
      })
    } else {
      // mark the whole svg as parsed
      markSvgAsParsed()
    }
  }

  static fetch(input, init = {}, useCache = true) {
    return new Promise((resolve) => {
      // try to get response from cache if possible
      // clone result to prevent "stream already read" errors
      if (useCache && this.fetchResponse.has(input)) {
        const response = this.fetchResponse.get(input)
        response.count++
        resolve(response.value.clone())
        return
      }

      // add request to pending list
      if (!this.fetchPending.has(input)) {
        this.fetchPending.set(input, {
          value: fetch(input, init),
          count: 0
        })
      }

      const pending = this.fetchPending.get(input)
      pending.count++

      // wait for response...
      pending
        .value
        .then((result) => {
          // store result in cache if necessary
          if (useCache) {
            this.fetchResponse.set(input, {
              value: result,
              count: pending.count
            })
          }

          // delete from pending list now we're done and return result
          // clone result to prevent "stream already read" errors
          this.fetchPending.delete(input)
          resolve(result.clone())
        })
    })
  }

  // eslint-disable-next-line no-magic-numbers
  static requestIdleCallback(callback, timeout = 1000) {
    // wait until browser has some idle time to execute callback, if supported...
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(() => callback(), {
        timeout
      })
    } else {
      // ... or fallback to a plain old set timeout
      setTimeout(() => callback(), timeout)
    }
  }

  static convertRemToPixels(rem) {
    return rem * parseFloat(getComputedStyle(document.documentElement).fontSize)
  }

  static getSanitizedMapBounds(bounds) {
    if (!Array.isArray(bounds)) {
      return null
    }

    // not enough coordinates or invalid coordinates type (must be: [south lat, south lng, north lat, north lng)
    const boundsLength = 4
    if (bounds.length !== boundsLength || bounds.some((v) => !AlphaHelpers.isNumber(v))) {
      return null
    }

    // invalid longitude (south must be smaller than north)
    if (bounds[1] > bounds[3]) {
      return null
    }

    return this.toMapBounds(bounds)
  }

  static toMapBounds(bounds) {
    return [
      [bounds[0], bounds[1]],
      [bounds[2], bounds[3]]
    ]
  }

  static getCryptLinkPrefix() {
    // eslint-disable-next-line no-script-url
    return 'javascript:DeCryptX'
  }

  static isCryptX(href) {
    return href.indexOf(this.getCryptLinkPrefix()) === 0
  }

  static decryptX(href) {
    if (!this.isCryptX(href)) {
      return href
    }

    const regExp = new RegExp(`${this.getCryptLinkPrefix()}\\('([^']+)'\\)`)
    const match = href.match(regExp)
    const expectedMatchLength = 2
    if (match.length === expectedMatchLength) {
      return DeCryptString(match[1])
    }

    return href
  }

  static getUniqueId(length = -1) {
    const base32 = 32
    const base16 = 16

    let uniqueId = String(
      Date.now().toString(base32) +
      Math.random().toString(base16)
    ).replace(/\./g, '')

    if (length > 0) {
      length = Math.min(uniqueId.length - 1, length)
      uniqueId = uniqueId.slice(length * -1)
    }

    return uniqueId
  }
}
